import OrderDTO from "@/dto/order/OrderDTO";
import OrderSort from "../enum/OrderSort";

/**
 * 按订单下单时间降序排列，其中不同店铺相同下单时间，按店铺顺序排列；相同店铺相同下单时间，按订单ID排序
 */
function getOrdersByTimeDesc(orders: OrderDTO[]) {
    return orders.sort((a, b) => {
        const aOrderTime = new Date(a.orderTime).getTime();
        const bOrderTime = new Date(b.orderTime).getTime();

        if (aOrderTime !== bOrderTime) {
            return bOrderTime - aOrderTime;
        }

        if (a.shopId !== b.shopId) {
            return 0;
        }

        if (a.id !== b.id) {
            return a.id - b.id;
        }

        return 0;
    });
}

/**
 * 按订单下单时间升序排列，其中不同店铺相同下单时间，按店铺顺序排列；相同店铺相同下单时间，按订单ID排序
 */
function getOrdersByTimeAsc(orders: OrderDTO[]) {
    return orders.sort((a, b) => {
        const aOrderTime = new Date(a.orderTime).getTime();
        const bOrderTime = new Date(b.orderTime).getTime();

        if (aOrderTime !== bOrderTime) {
            return aOrderTime - bOrderTime;
        }

        if (a.shopId !== b.shopId) {
            return 0;
        }

        if (a.id !== b.id) {
            return a.id - b.id;
        }

        return 0;
    });
}

/**
 * 按订单付款时间降序排列，其中不同店铺相同付款时间，按店铺顺序排列；相同店铺相同付款时间，按订单ID排序
 */
function getOrdersByPayTimeDesc(orders: OrderDTO[]) {
    return orders.sort((a, b) => {
        const aTime = new Date(a.paymentTime).getTime();
        const bTime = new Date(b.paymentTime).getTime();

        if (aTime !== bTime) {
            return bTime - aTime;
        }

        if (a.shopId !== b.shopId) {
            return 0;
        }

        if (a.id !== b.id) {
            return a.id - b.id;
        }

        return 0;
    });
}

/**
 * 按订单付款时间升序排列，其中不同店铺相同付款时间，按店铺顺序排列；相同店铺相同付款时间，按订单ID排序
 */
function getOrdersByPayTimeAsc(orders: OrderDTO[]) {
    return orders.sort((a, b) => {
        const aTime = new Date(a.paymentTime).getTime();
        const bTime = new Date(b.paymentTime).getTime();

        if (aTime !== bTime) {
            return aTime - bTime;
        }

        if (a.shopId !== b.shopId) {
            return 0;
        }

        if (a.id !== b.id) {
            return a.id - b.id;
        }

        return 0;
    });
}

/**
 * 按相同商品排序
 */
function getOrdersByOrderItems(orders: OrderDTO[]) {
    return orders.sort((a, b) => {
        const aNum = a.sortIndex || 0;
        const bNum = b.sortIndex || 0;

        if (aNum !== bNum) {
            return aNum - bNum;
        }

        if (a.shopId !== b.shopId) {
            return 0;
        }

        if (a.id !== b.id) {
            return a.id - b.id;
        }

        return 0;
    });
}

/**
 * 按订单实付金额降序排列，其中不同店铺相同实付金额，按店铺顺序排列；相同店铺相同实付金额，按订单ID排序
 */
function getOrdersByPaymentAmountDesc(orders: OrderDTO[]) {
    return orders.sort((a, b) => {
        const aNum = a.payments;
        const bNum = b.payments;

        if (aNum !== bNum) {
            return bNum - aNum;
        }

        if (a.shopId !== b.shopId) {
            return 0;
        }

        if (a.id !== b.id) {
            return a.id - b.id;
        }

        return 0;
    });
}

/**
 * 按订单的流水号升序排列，其中不同店铺相同流水号，按店铺顺序排列；相同店铺相同流水号，按订单ID排序
 */
function getOrdersBySequenceNo(orders: OrderDTO[]) {
    return orders.sort((a, b) => {
        const aNum = a.sequenceNo || 0;
        const bNum = b.sequenceNo || 0;

        if (aNum !== bNum) {
            return aNum - bNum;
        }

        if (a.shopId !== b.shopId) {
            return 0;
        }

        if (a.id !== b.id) {
            return a.id - b.id;
        }

        return 0;
    });
}

const maxTime = new Date("2100-01-01 00:00:00");
/**
 * 按订单的最后发货时间升序排列，其中不同店铺相同最后发货时间，按店铺顺序排列；相同店铺相同最后发货时间，按订单ID排序
 */
function getOrdersByLastShippingTimeAsc(orders: OrderDTO[]) {
    return orders.sort((a, b) => {
        const aNum = new Date(a.shippingDeadline || maxTime).getTime();
        const bNum = new Date(b.shippingDeadline || maxTime).getTime();

        if (aNum !== bNum) {
            return aNum - bNum;
        }

        if (a.shopId !== b.shopId) {
            return 0;
        }

        if (a.id !== b.id) {
            return a.id - b.id;
        }

        return 0;
    });
}

/**
 * 按商品编码排序，其中不同商品编码的订单按排序索引升序排列；相同商品编码的订单，按规格编码排序索引升序排列；
 * 相同规格编码的订单，按SKU平台ID排序值升序；相同店铺ID和其他条件相同的订单，按订单ID升序排列
 */
function getOrdersByProductSkuExternalId(orders: OrderDTO[]) {
    return orders.sort((a, b) => {
        const aProductExternalSortIndex = a.productExternalIdSortIndex || 0;
        const bProductExternalSortIndex = b.productExternalIdSortIndex || 0;

        if (aProductExternalSortIndex !== bProductExternalSortIndex) {
            return aProductExternalSortIndex - bProductExternalSortIndex;
        }

        const aSkuExternalSortIndex = a.skuExternalIdSortIndex || 0;
        const bSkuExternalSortIndex = b.skuExternalIdSortIndex || 0;

        if (aSkuExternalSortIndex !== bSkuExternalSortIndex) {
            return aSkuExternalSortIndex - bSkuExternalSortIndex;
        }

        const aSkuOriginalIdSortIndex = a.skuOriginalIdSortIndex || 0;
        const bSkuOriginalIdSortIndex = b.skuOriginalIdSortIndex || 0;

        if (aSkuOriginalIdSortIndex !== bSkuOriginalIdSortIndex) {
            return aSkuOriginalIdSortIndex - bSkuOriginalIdSortIndex;
        }

        if (a.shopId !== b.shopId) {
            return 0;
        }

        if (a.id !== b.id) {
            return a.id - b.id;
        }

        return 0;
    });
}

/**
 * 按规格编码排序索引升序排列；
 * 相同规格编码的订单，按SKU平台ID排序值升序；
 * 相同店铺ID和其他条件相同的订单，按订单ID升序排列
 */
function getOrdersBySkuExternalId(orders: OrderDTO[]) {
    return orders.sort((a, b) => {
        const aSkuExternalSortIndex = a.skuExternalIdSortIndex || 0;
        const bSkuExternalSortIndex = b.skuExternalIdSortIndex || 0;

        if (aSkuExternalSortIndex !== bSkuExternalSortIndex) {
            return aSkuExternalSortIndex - bSkuExternalSortIndex;
        }

        const aSkuOriginalIdSortIndex = a.skuOriginalIdSortIndex || 0;
        const bSkuOriginalIdSortIndex = b.skuOriginalIdSortIndex || 0;

        if (aSkuOriginalIdSortIndex !== bSkuOriginalIdSortIndex) {
            return aSkuOriginalIdSortIndex - bSkuOriginalIdSortIndex;
        }

        if (a.shopId !== b.shopId) {
            return 0;
        }

        if (a.id !== b.id) {
            return a.id - b.id;
        }

        return 0;
    });
}

/**
 *按订单的重量升序排列，其中不同店铺相同重量，按店铺顺序排列；相同店铺相同重量，按订单ID排序
 */
function getOrdersByOrderWeight(orders: OrderDTO[]) {
    return orders.sort((a, b) => {
        const aWeight = a.weight || 0;
        const bWeight = b.weight || 0;

        if (aWeight !== bWeight) {
            return aWeight - bWeight;
        }

        if (a.shopId !== b.shopId) {
            return 0;
        }

        if (a.id !== b.id) {
            return a.id - b.id;
        }

        return 0;
    });
}

/**
 *按订单的省市区逐个升序排列，其中不同店铺相同省市区，按店铺顺序排列；相同店铺相同省市区，按订单ID排序
 */
function getOrdersByOrderConsigneeRegion(orders: OrderDTO[]) {
    return orders.sort((a, b) => {
        const aProvinceCode = a.provinceCode || 0;
        const bProvinceCode = b.provinceCode || 0;

        if (aProvinceCode !== bProvinceCode) {
            return aProvinceCode - bProvinceCode;
        }

        const aCityCode = a.cityCode || 0;
        const bCityCode = b.cityCode || 0;

        if (aCityCode !== bCityCode) {
            return aCityCode - bCityCode;
        }

        const aDistrictCode = a.districtCode || 0;
        const bDistrictCode = b.districtCode || 0;

        if (aDistrictCode !== bDistrictCode) {
            return aDistrictCode - bDistrictCode;
        }

        if (a.shopId !== b.shopId) {
            return 0;
        }

        if (a.id !== b.id) {
            return a.id - b.id;
        }

        return 0;
    });
}

/**
 *按订单的数量升序排列，其中不同店铺相同数量，按店铺顺序排列；相同店铺相同数量，按订单ID排序
 */
function getOrdersByOrderItemQuantityAsc(orders: OrderDTO[]) {
    return orders.sort((a, b) => {
        const aQuantity = a.quantity || 0;
        const bQuantity = b.quantity || 0;

        if (aQuantity !== bQuantity) {
            return aQuantity - bQuantity;
        }

        if (a.shopId !== b.shopId) {
            return 0;
        }

        if (a.id !== b.id) {
            return a.id - b.id;
        }

        return 0;
    });
}

/**
 *按订单的数量降序排列，其中不同店铺相同数量，按店铺顺序排列；相同店铺相同数量，按订单ID排序
 */
function getOrdersByOrderItemQuantityDesc(orders: OrderDTO[]) {
    return orders.sort((a, b) => {
        const aQuantity = a.quantity || 0;
        const bQuantity = b.quantity || 0;

        if (aQuantity !== bQuantity) {
            return bQuantity - aQuantity;
        }

        if (a.shopId !== b.shopId) {
            return 0;
        }

        if (a.id !== b.id) {
            return a.id - b.id;
        }

        return 0;
    });
}

/**
 * 按规格简称规格/名称排序
 * 排序优先级如下:
 * 1.skuSimpleTitleSortIndex
 * 2.skuPropertiesSortIndex
 * 3.productOriginalIdSortIndex
 * 4.skuOriginalIdSortIndex
 * 5.shopId
 * 6.orderId
 */

/**
 * 按(规格简称规格/名称)排序
 * 按(规格简称或名称)任一排序值升序排列；
 * 相同商品平台ID排序索引的订单，按SKU平台ID排序索引升序；
 * 相同店铺ID和其他条件相同的订单，按订单ID升序排列
 */
function getOrdersBySimpleTitleOtherwiseSkuProperties(orders: OrderDTO[]) {
    return orders.sort((a, b) => {
        const aSkuSimpleTitlePropertiesSortIndexStr =
            a.skuSimpleTitleSortIndex?.toString() || a.skuPropertiesSortHash?.toString() || "";
        const bSkuSimpleTitlePropertiesSortIndexStr =
            b.skuSimpleTitleSortIndex?.toString() || b.skuPropertiesSortHash?.toString() || "";

        if (aSkuSimpleTitlePropertiesSortIndexStr !== bSkuSimpleTitlePropertiesSortIndexStr) {
            return aSkuSimpleTitlePropertiesSortIndexStr.localeCompare(bSkuSimpleTitlePropertiesSortIndexStr);
        }

        const aProductOriginalIdSortIndex = a.productOriginalIdSortIndex || 0;
        const bProductOriginalIdSortIndex = b.productOriginalIdSortIndex || 0;

        if (aProductOriginalIdSortIndex !== bProductOriginalIdSortIndex) {
            return aProductOriginalIdSortIndex - bProductOriginalIdSortIndex;
        }

        const aSkuOriginalIdSortIndex = a.skuOriginalIdSortIndex || 0;
        const bSkuOriginalIdSortIndex = b.skuOriginalIdSortIndex || 0;

        if (aSkuOriginalIdSortIndex !== bSkuOriginalIdSortIndex) {
            return aSkuOriginalIdSortIndex - bSkuOriginalIdSortIndex;
        }

        if (a.shopId !== b.shopId) {
            return 0;
        }

        if (a.id !== b.id) {
            return a.id - b.id;
        }

        return 0;
    });
}

/**
 * 返回一个浅拷贝的新数组
 */
export default function getOrdersByLocalSort(orders: OrderDTO[] = [], orderSort?: OrderSort) {
    const newOrders = orders.slice();

    switch (orderSort) {
        case OrderSort.ORDER_TIME_DESC:
            return getOrdersByTimeDesc(newOrders);
        case OrderSort.ORDER_TIME_ASC:
            return getOrdersByTimeAsc(newOrders);
        case OrderSort.PAY_TIME_DESC:
            return getOrdersByPayTimeDesc(newOrders);
        case OrderSort.PAY_TIME_ASC:
            return getOrdersByPayTimeAsc(newOrders);
        case OrderSort.PRODUCT_SKU_EXTERNAL_ID:
            return getOrdersByProductSkuExternalId(newOrders);
        case OrderSort.SKU_EXTERNAL_ID:
            return getOrdersBySkuExternalId(newOrders);
        case OrderSort.ORDER_ITEMS:
            return getOrdersByOrderItems(newOrders);
        case OrderSort.ORDER_ITEM_QUANTITY:
            return getOrdersByOrderItemQuantityAsc(newOrders);
        case OrderSort.ORDER_ITEM_QUANTITY_DESC:
            return getOrdersByOrderItemQuantityDesc(newOrders);
        case OrderSort.PAYMENT_AMOUNT_DESC:
            return getOrdersByPaymentAmountDesc(newOrders);
        case OrderSort.ORDER_WEIGHT:
            return getOrdersByOrderWeight(newOrders);
        case OrderSort.ORDER_CONSIGNEE_REGION:
            return getOrdersByOrderConsigneeRegion(newOrders);
        case OrderSort.SEQUENCE_NO:
            return getOrdersBySequenceNo(newOrders);
        case OrderSort.LAST_SHIPPING_TIME_ASC:
            return getOrdersByLastShippingTimeAsc(newOrders);
        case OrderSort.SIMPLE_TITLE_OTHERWISE_SKU_PROPERTIES:
            return getOrdersBySimpleTitleOtherwiseSkuProperties(newOrders);
        default:
            return newOrders;
    }
}
